/**
 * 1OS Shared Date Range Contract
 * ------------------------------
 *
 * Single source of truth for the date-range filter shared across
 * Sales Overview, Sales Explorer, and any future analytics page.
 *
 * Rules (NEVER violate — these caused founder-visible regressions
 * twice already, see /lib/date.js for context):
 *   1. ALL date values are LOCAL-TZ `YYYY-MM-DD` strings (never UTC).
 *   2. URL contract is exactly two params: `date_from`, `date_to`.
 *   3. Default landing range is THIS MONTH (clamped to today, never
 *      future-tail) when both URL params are missing or invalid.
 *   4. NO `localStorage` stickiness. A shared link must reproduce the
 *      same range identically for any user.
 *   5. The `custom` preset is a non-selectable bucket label — selected
 *      only when the active range matches no other preset.
 */
import { fmtLocalISO, todayISO, isoMinusDays } from './date';

export const RANGE_PRESETS = [
  { key: 'today',     label: 'Today'      },
  { key: 'yesterday', label: 'Yesterday'  },
  { key: '7d',        label: 'Last 7d'    },
  { key: '14d',       label: 'Last 14d'   },
  { key: '30d',       label: 'Last 30d'   },
  { key: '90d',       label: 'Last 90d'   },
  { key: 'this',      label: 'This Month' },
  { key: 'last',      label: 'Last Month' },
  { key: 'custom',    label: 'Custom'     },
];

/**
 * Resolve calendar month boundaries.
 *   offset  0  → this month (clamped to today so we never query the
 *                future-tail days that have no data).
 *   offset -1  → last month (always complete).
 */
export function monthBoundaries(offset = 0) {
  const today = new Date();
  const ref = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const lastOfMonth = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  const end = (offset === 0 && lastOfMonth > today) ? today : lastOfMonth;
  return { from: fmtLocalISO(first), to: fmtLocalISO(end) };
}

/** Map a preset key to a concrete `{from, to}` range. */
export function rangeFromPreset(key) {
  if (key === 'today')     return { from: todayISO(),     to: todayISO()     };
  if (key === 'yesterday') return { from: isoMinusDays(1), to: isoMinusDays(1) };
  if (key === '7d')   return { from: isoMinusDays(6),  to: todayISO() };
  if (key === '14d')  return { from: isoMinusDays(13), to: todayISO() };
  if (key === '30d')  return { from: isoMinusDays(29), to: todayISO() };
  if (key === '90d')  return { from: isoMinusDays(89), to: todayISO() };
  if (key === 'this') return monthBoundaries(0);
  if (key === 'last') return monthBoundaries(-1);
  // 'custom' has no implicit range — fall back to This Month so a click
  // on Custom without a date selection still lands the user somewhere
  // sane (rather than an empty or stale window).
  return monthBoundaries(0);
}

/** Reverse-derive the preset key for an applied `{from, to}` range. */
export function presetFromRange(from, to) {
  for (const p of RANGE_PRESETS) {
    if (p.key === 'custom') continue;
    const r = rangeFromPreset(p.key);
    if (r.from === from && r.to === to) return p.key;
  }
  return 'custom';
}

/** Strict ISO `YYYY-MM-DD` validator. */
const ISO_RX = /^\d{4}-\d{2}-\d{2}$/;
export const isValidISO = (s) =>
  typeof s === 'string' && ISO_RX.test(s) && !Number.isNaN(new Date(s).getTime());

/** Pretty-print an ISO date as `DD-MM-YYYY` for display only. */
export const fmtDDMMYYYY = (iso) => {
  if (!isValidISO(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

/**
 * Resolve the initial date range for a page from URL search params.
 * Returns `{from, to, source}` where `source` is `'url'` if both
 * `date_from` and `date_to` were present + valid, else `'default'`.
 *
 * This is the SINGLE entry point every page should use — keeps the
 * "default = This Month" rule in one place.
 */
export function resolveInitialRange(searchParams) {
  const urlFrom = searchParams.get('date_from');
  const urlTo   = searchParams.get('date_to');
  if (isValidISO(urlFrom) && isValidISO(urlTo)) {
    return { from: urlFrom, to: urlTo, source: 'url' };
  }
  const defaults = rangeFromPreset('this');
  return { from: defaults.from, to: defaults.to, source: 'default' };
}
